// Reachvel — legal copy.
// Each "section" has a numbered heading and a list of nodes.
// Node types:  { type: "p", text } | { type: "ul", items: [string|{label, value}] } | { type: "h3", text }

export const PRIVACY_POLICY = {
  effective: "May 10, 2026",
  version: "1.0",
  sections: [
    {
      number: "01",
      title: "Introduction",
      nodes: [
        { type: "p", text: "Reachvel (\u201cCompany\u201d, \u201cwe\u201d, \u201cour\u201d, or \u201cus\u201d) is committed to protecting your privacy and handling your personal information with transparency, integrity, and respect. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website (www.reachvel.com), use our services, or interact with us in any capacity." },
        { type: "p", text: "By accessing or using our services, you acknowledge that you have read, understood, and consent to the practices described in this Privacy Policy. If you do not agree with the terms herein, please discontinue use of our services immediately." },
      ],
    },
    {
      number: "02",
      title: "Information We Collect",
      nodes: [
        { type: "h3", text: "2.1 Information you provide directly" },
        { type: "p", text: "We collect personal information that you voluntarily provide to us when you:" },
        { type: "ul", items: [
          "Register for an account or subscribe to our services",
          "Submit inquiries, contact forms, or support requests",
          "Engage in business transactions or sign service agreements",
          "Participate in surveys, feedback forms, or promotional activities",
          "Correspond with us via email, phone, or other communication channels",
        ]},
        { type: "p", text: "This may include: full name, email address, phone number, company name, billing address, payment details, and any other information you choose to share." },
        { type: "h3", text: "2.2 Information collected automatically" },
        { type: "p", text: "When you access our website or services, we automatically collect certain technical information, including:" },
        { type: "ul", items: [
          "IP address and approximate geographic location",
          "Browser type, version, and operating system",
          "Pages visited, time spent on pages, and navigation patterns",
          "Referring URLs and exit pages",
          "Device identifiers and cookie data",
        ]},
        { type: "h3", text: "2.3 Information from third parties" },
        { type: "p", text: "We may receive information about you from third-party sources such as business partners, analytics providers, social media platforms, and publicly available databases, in accordance with applicable laws and their respective privacy policies." },
      ],
    },
    {
      number: "03",
      title: "How We Use Your Information",
      nodes: [
        { type: "p", text: "We use the information we collect for the following lawful purposes:" },
        { type: "ul", items: [
          "Delivering, operating, and improving our products and services",
          "Processing transactions and managing billing and contracts",
          "Providing customer support and responding to inquiries",
          "Communicating service updates, security alerts, and administrative notices",
          "Sending marketing communications (where consent has been obtained)",
          "Analyzing usage patterns to enhance user experience and service quality",
          "Complying with legal obligations, regulations, and contractual commitments",
          "Detecting, preventing, and addressing fraud, abuse, and security incidents",
          "Conducting research and development for new features and services",
        ]},
      ],
    },
    {
      number: "04",
      title: "Legal Basis for Processing (GDPR & Global Compliance)",
      nodes: [
        { type: "p", text: "For users in the European Economic Area (EEA), United Kingdom, and other jurisdictions with similar legislation, our processing of personal data is based on one or more of the following legal grounds:" },
        { type: "ul", items: [
          "Contractual necessity \u2014 to perform a contract you have entered into with us",
          "Legitimate interests \u2014 to operate our business, prevent fraud, and improve our services, provided these interests are not overridden by your rights",
          "Consent \u2014 where you have given us explicit permission for specific processing activities",
          "Legal obligation \u2014 to comply with applicable laws and regulatory requirements",
        ]},
      ],
    },
    {
      number: "05",
      title: "Disclosure of Your Information",
      nodes: [
        { type: "h3", text: "5.1 Service providers" },
        { type: "p", text: "We engage trusted third-party service providers who perform functions on our behalf, including cloud hosting, payment processing, analytics, email delivery, and customer support tools. These providers are contractually obligated to maintain the confidentiality and security of your information and may only use it for the specific purposes we authorize." },
        { type: "h3", text: "5.2 Business transfers" },
        { type: "p", text: "In the event of a merger, acquisition, restructuring, or sale of all or a portion of our assets, your personal information may be transferred as part of that transaction. We will notify affected users prior to any such transfer." },
        { type: "h3", text: "5.3 Legal requirements" },
        { type: "p", text: "We may disclose your information if required by law, court order, or governmental authority, or where we believe in good faith that such disclosure is necessary to protect our rights, your safety, or the safety of others." },
        { type: "h3", text: "5.4 Aggregate & anonymised data" },
        { type: "p", text: "We may share anonymised, aggregated, or de-identified data \u2014 which cannot reasonably identify you \u2014 for research, analytics, marketing, and business development purposes." },
      ],
    },
    {
      number: "06",
      title: "Cookies and Tracking Technologies",
      nodes: [
        { type: "p", text: "We use cookies, web beacons, pixels, and similar tracking technologies to enhance your experience and gather usage insights. You may control or disable cookies through your browser settings; however, doing so may affect the functionality of certain features. Our cookie usage includes: essential cookies (required for site operation), performance cookies (analytics and optimisation), functional cookies (personalisation), and targeting cookies (where consent is obtained for marketing purposes)." },
      ],
    },
    {
      number: "07",
      title: "Data Retention",
      nodes: [
        { type: "p", text: "We retain your personal information for as long as necessary to fulfil the purposes outlined in this Policy, unless a longer retention period is required or permitted by law. Retention periods are determined by the nature of the data, the purpose of collection, and applicable legal or regulatory obligations. Upon expiry of the applicable retention period, data is securely deleted or anonymised." },
      ],
    },
    {
      number: "08",
      title: "Data Security",
      nodes: [
        { type: "p", text: "We implement industry-standard administrative, technical, and physical safeguards to protect your personal information against unauthorised access, disclosure, alteration, or destruction. These include encryption in transit and at rest, access controls, regular security assessments, and employee training. While we employ robust security measures, no transmission over the internet or electronic storage method is 100% secure. We cannot guarantee absolute security, but we are committed to promptly notifying affected individuals in the event of a data breach as required by applicable law." },
      ],
    },
    {
      number: "09",
      title: "Your Privacy Rights",
      nodes: [
        { type: "p", text: "Depending on your jurisdiction, you may have the following rights regarding your personal data:" },
        { type: "ul", items: [
          "Right of Access \u2014 to obtain confirmation of whether we process your data and receive a copy",
          "Right to Rectification \u2014 to correct inaccurate or incomplete personal data",
          "Right to Erasure \u2014 to request deletion of your personal data under certain conditions",
          "Right to Restriction \u2014 to restrict how we process your data in certain circumstances",
          "Right to Data Portability \u2014 to receive your data in a structured, machine-readable format",
          "Right to Object \u2014 to object to processing based on legitimate interests or for direct marketing",
          "Right to Withdraw Consent \u2014 at any time, where processing is based on consent",
          "Right to Lodge a Complaint \u2014 with the relevant data protection authority in your jurisdiction",
        ]},
        { type: "p", text: "To exercise any of these rights, please contact us at privacy@reachvel.com. We will respond within the timeframes required by applicable law (typically 30 days)." },
      ],
    },
    {
      number: "10",
      title: "International Data Transfers",
      nodes: [
        { type: "p", text: "Reachvel operates globally. Your information may be transferred to, stored, and processed in countries other than your country of residence, including countries that may not provide the same level of data protection as your home jurisdiction. Where such transfers occur, we ensure appropriate safeguards are in place, including Standard Contractual Clauses, data processing agreements, or reliance on adequacy decisions." },
      ],
    },
    {
      number: "11",
      title: "Children's Privacy",
      nodes: [
        { type: "p", text: "Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from minors. If we become aware that a minor has provided us with personal data without parental consent, we will take steps to delete such information promptly. If you believe we may have inadvertently collected information from a minor, please contact us immediately." },
      ],
    },
    {
      number: "12",
      title: "Third-Party Links and Integrations",
      nodes: [
        { type: "p", text: "Our website and services may contain links to third-party websites, integrations, or platforms. This Privacy Policy does not apply to those third-party services. We encourage you to review the privacy policies of any third-party sites you visit, as we have no control over their practices and assume no responsibility for their content or data handling." },
      ],
    },
    {
      number: "13",
      title: "Changes to This Privacy Policy",
      nodes: [
        { type: "p", text: "We reserve the right to update or modify this Privacy Policy at any time to reflect changes in our practices, legal requirements, or service offerings. We will notify you of material changes by posting the updated Policy on our website with a revised effective date, and \u2014 where appropriate \u2014 by direct notification via email. Your continued use of our services after the effective date constitutes your acceptance of the revised Policy." },
      ],
    },
  ],
};

export const TERMS_OF_USE = {
  effective: "May 10, 2026",
  version: "1.0",
  sections: [
    {
      number: "01",
      title: "Acceptance of Terms",
      nodes: [
        { type: "p", text: "These Terms of Use (\u201cTerms\u201d) constitute a legally binding agreement between you (\u201cUser\u201d, \u201cyou\u201d, or \u201cyour\u201d) and Reachvel (\u201cCompany\u201d, \u201cwe\u201d, \u201cour\u201d, or \u201cus\u201d) governing your access to and use of our website located at www.reachvel.com and all associated services, platforms, applications, and content (collectively, the \u201cServices\u201d)." },
        { type: "p", text: "By accessing, browsing, or using our Services, you affirm that you are at least 18 years of age, have the legal capacity to enter into binding agreements, and agree to be bound by these Terms in their entirety." },
        { type: "p", text: "If you are accessing the Services on behalf of a company or legal entity, you represent and warrant that you have the authority to bind that entity to these Terms. If you do not agree to these Terms, you must immediately cease all use of our Services." },
      ],
    },
    {
      number: "02",
      title: "Description of Services",
      nodes: [
        { type: "p", text: "Reachvel is an AI services and software solutions company providing a broad range of technology services including, but not limited to:" },
        { type: "ul", items: [
          "Web development and mobile application development",
          "AI automation, integration, and consulting services",
          "E-commerce solutions and digital product development",
          "Data analytics, business intelligence, and QA services",
          "Software-as-a-Service (SaaS) products and platforms",
          "Technology strategy and digital transformation advisory",
        ]},
        { type: "p", text: "We reserve the right to modify, suspend, expand, or discontinue any part of our Services at any time without prior notice. We shall not be liable to you or any third party for any such modification, suspension, or discontinuation." },
      ],
    },
    {
      number: "03",
      title: "User Accounts and Registration",
      nodes: [
        { type: "p", text: "Certain features of our Services require registration. When creating an account, you agree to:" },
        { type: "ul", items: [
          "Provide accurate, current, and complete information",
          "Maintain and promptly update your account information",
          "Keep your login credentials confidential and secure",
          "Notify us immediately of any unauthorised use of your account",
          "Accept responsibility for all activities conducted under your account",
        ]},
        { type: "p", text: "We reserve the right to suspend or terminate accounts that contain false information, violate these Terms, or engage in any activity that may harm Reachvel or its users." },
      ],
    },
    {
      number: "04",
      title: "Acceptable Use Policy",
      nodes: [
        { type: "h3", text: "4.1 Permitted use" },
        { type: "p", text: "You may use our Services solely for lawful purposes and in accordance with these Terms. You agree not to use the Services in any way that violates applicable local, national, or international laws or regulations." },
        { type: "h3", text: "4.2 Prohibited activities" },
        { type: "p", text: "You expressly agree not to:" },
        { type: "ul", items: [
          "Use our Services for any unlawful, fraudulent, or deceptive purpose",
          "Transmit unsolicited communications, spam, or commercial messages without consent",
          "Distribute malware, viruses, or any code designed to disrupt or harm systems",
          "Attempt to gain unauthorised access to our systems, networks, or user accounts",
          "Scrape, mine, or harvest data from our Services without written permission",
          "Reverse engineer, decompile, or disassemble any component of our Services",
          "Infringe upon intellectual property rights of Reachvel or any third party",
          "Engage in conduct that restricts or inhibits any other person's use of the Services",
          "Impersonate Reachvel, its employees, or any other person or entity",
          "Post or transmit content that is defamatory, obscene, hateful, or otherwise objectionable",
        ]},
        { type: "p", text: "Reachvel reserves the right to investigate suspected violations and take appropriate action, including removal of content, suspension of accounts, and referral to law enforcement authorities." },
      ],
    },
    {
      number: "05",
      title: "Intellectual Property Rights",
      nodes: [
        { type: "p", text: "All content, materials, features, and functionality available through our Services \u2014 including but not limited to source code, software, design, graphics, logos, text, data, documentation, and trademarks \u2014 are the exclusive property of Reachvel or its licensors and are protected by applicable intellectual property laws worldwide." },
        { type: "p", text: "Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, revocable licence to access and use our Services for your personal or internal business purposes. This licence does not include any right to:" },
        { type: "ul", items: [
          "Reproduce, distribute, or publicly display our content without prior written consent",
          "Create derivative works based on our Services or content",
          "Use our trademarks, service marks, or branding in any manner without authorisation",
          "Sub-license, sell, resell, or otherwise commercially exploit any part of our Services",
        ]},
      ],
    },
    {
      number: "06",
      title: "User-Submitted Content",
      nodes: [
        { type: "p", text: "You may submit content, data, files, or other materials to us in connection with your use of our Services (\u201cUser Content\u201d). You retain ownership of your User Content. By submitting User Content, you grant Reachvel a non-exclusive, worldwide, royalty-free licence to use, process, store, and transmit such content solely to the extent necessary to provide and improve the Services." },
        { type: "p", text: "You represent and warrant that: (a) you have all rights necessary to grant the above licence; (b) your User Content does not infringe any third-party rights; and (c) your User Content complies with all applicable laws and these Terms." },
      ],
    },
    {
      number: "07",
      title: "Fees, Payments, and Billing",
      nodes: [
        { type: "p", text: "Certain Services are provided on a paid basis. All fees are specified in the applicable service agreement, proposal, or order form. Unless otherwise stated, fees are non-refundable." },
        { type: "p", text: "You agree to pay all applicable fees in the currency specified and in accordance with the agreed payment schedule. We reserve the right to modify our pricing at any time. For existing contracts, pricing changes will take effect upon contract renewal or as otherwise agreed in writing." },
        { type: "p", text: "Reachvel shall not be liable for any financial losses arising from currency conversion or exchange rate fluctuations." },
      ],
    },
    {
      number: "08",
      title: "Confidentiality",
      nodes: [
        { type: "p", text: "Each party may have access to confidential information of the other party in connection with the Services. Both parties agree to: (a) hold all confidential information in strict confidence; (b) use confidential information only for the purposes of the engagement; and (c) not disclose confidential information to third parties without prior written consent, except as required by law." },
        { type: "p", text: "This obligation of confidentiality shall survive the termination or expiry of these Terms and any associated service agreement for a period of three (3) years, unless a longer period is specified in a separate agreement." },
      ],
    },
    {
      number: "09",
      title: "Disclaimers and Limitation of Liability",
      nodes: [
        { type: "h3", text: "9.1 No warranties" },
        { type: "p", text: "Our Services are provided \u201cas is\u201d and \u201cas available\u201d without warranties of any kind, express or implied. To the fullest extent permitted by applicable law, Reachvel expressly disclaims all warranties, including but not limited to implied warranties of merchantability, fitness for a particular purpose, non-infringement, and uninterrupted or error-free operation." },
        { type: "h3", text: "9.2 Limitation of liability" },
        { type: "p", text: "To the maximum extent permitted by law, Reachvel, its directors, officers, employees, affiliates, and licensors shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, goodwill, or business interruption, arising from your use of or inability to use our Services, even if advised of the possibility of such damages." },
        { type: "p", text: "In no event shall Reachvel's total aggregate liability to you exceed the greater of (a) the amount paid by you to Reachvel in the twelve (12) months preceding the claim, or (b) INR 10,000 (or equivalent)." },
      ],
    },
    {
      number: "10",
      title: "Indemnification",
      nodes: [
        { type: "p", text: "You agree to indemnify, defend, and hold harmless Reachvel and its officers, directors, employees, agents, and affiliates from and against any claims, liabilities, damages, losses, costs, and expenses (including reasonable legal fees) arising out of or in connection with: (a) your use of or access to our Services; (b) your violation of these Terms; (c) your User Content; or (d) your infringement of any third-party rights." },
      ],
    },
    {
      number: "11",
      title: "Termination",
      nodes: [
        { type: "p", text: "These Terms are effective until terminated. We may terminate or suspend your access to our Services immediately, without prior notice or liability, if you breach any provision of these Terms or if we discontinue the relevant Service." },
        { type: "p", text: "Upon termination: (a) all licences granted under these Terms will immediately cease; (b) we may delete or disable your account and associated data in accordance with our data retention policy; and (c) provisions that by their nature should survive termination shall remain in effect, including Sections 5, 8, 9, 10, 12, and 13." },
      ],
    },
    {
      number: "12",
      title: "Governing Law and Dispute Resolution",
      nodes: [
        { type: "p", text: "These Terms shall be governed by and construed in accordance with the laws of India, without regard to its conflict of law provisions." },
        { type: "p", text: "Any disputes arising under or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts located in Hyderabad, Telangana, India." },
        { type: "p", text: "Before initiating formal proceedings, the parties agree to attempt resolution of any dispute through good-faith negotiation. If unresolved within 30 days, disputes may be submitted to arbitration in accordance with the Arbitration and Conciliation Act, 1996 (India), or as mutually agreed in writing." },
        { type: "p", text: "For users located in the European Union or United Kingdom, nothing in these Terms affects your statutory rights as a consumer under applicable EU or UK consumer protection legislation." },
      ],
    },
    {
      number: "13",
      title: "General Provisions",
      nodes: [
        { type: "h3", text: "13.1 Entire agreement" },
        { type: "p", text: "These Terms, together with our Privacy Policy and any applicable service agreements, constitute the entire agreement between you and Reachvel with respect to the subject matter herein and supersede all prior or contemporaneous communications and proposals." },
        { type: "h3", text: "13.2 Severability" },
        { type: "p", text: "If any provision of these Terms is found to be invalid, unlawful, or unenforceable by a court of competent jurisdiction, that provision shall be modified to the minimum extent necessary to make it enforceable, and the remaining provisions shall continue in full force and effect." },
        { type: "h3", text: "13.3 Waiver" },
        { type: "p", text: "No failure or delay by Reachvel in exercising any right under these Terms shall constitute a waiver of that right. Any waiver must be in writing and signed by an authorised representative of Reachvel." },
        { type: "h3", text: "13.4 Assignment" },
        { type: "p", text: "You may not assign or transfer your rights or obligations under these Terms without our prior written consent. Reachvel may assign these Terms in connection with a merger, acquisition, or sale of assets without restriction." },
        { type: "h3", text: "13.5 Force majeure" },
        { type: "p", text: "Reachvel shall not be liable for any failure or delay in performance resulting from causes beyond our reasonable control, including but not limited to acts of God, natural disasters, wars, pandemics, cyberattacks, or government actions." },
      ],
    },
    {
      number: "14",
      title: "Amendments to These Terms",
      nodes: [
        { type: "p", text: "We reserve the right to revise these Terms at any time. When we make material changes, we will update the effective date at the top of this document and, where appropriate, provide notice via our website or by email." },
        { type: "p", text: "Your continued use of the Services after the effective date of any amended Terms constitutes your acceptance of those changes. We encourage you to review these Terms periodically to stay informed of any updates." },
      ],
    },
  ],
};
